import { useState } from 'react';
import {
  Calculator, Loader2, Plus, Trash2, X, Pencil, CheckCircle2, FileDown,
} from 'lucide-react';
import type {
  AbrechnungResponse, AbrechnungSettings, AbrechnungSettingsPatch,
  NachtragRow, BnkPosition, ZahlungsplanRow,
  NachtragPayload, BnkPayload, ZahlungsplanPayload,
} from '@/types';
import { abrechnungApi } from '@/services/api';
import { formatCurrency, formatDateTime } from '@/utils/currency';
import { parseGermanDecimal } from '@/utils/number';

function apiError(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}

interface BauvorhabenAbrechnungCardProps {
  bauvorhabenId: number;
  data: AbrechnungResponse | null;
  /** Schreiben: nur Finanzen/Admin (Server-Gate bleibt). */
  canWrite: boolean;
  /** Nach Mutationen: Einkauf-Zeilen nachladen (Herstellsumme ändert sich). */
  onChanged: () => void;
  /** Vollständige Nachladung (Settings-Änderungen + Formelkette). */
  onReloaded: (data: AbrechnungResponse) => void;
}

/**
 * Abrechnung-Karte (Phase 3): Excel-Abrechnungsprozess am BV — Kopfzeile
 * (Verkaufssummen, Fehlpositionen, Vorplanungszuschlag, Provision, AGK/MWB-
 * Schlüssel), transparente Formelkette (Herstellkosten → AGK-Split → G/V),
 * Nachträge/Gutschriften, allgemeine BNK (VK + EK) und Zahlungsplan
 * (Ausgangsrechnungen + Vertriebsprovision). Editieren nur Finanzen.
 */
export function BauvorhabenAbrechnungCard({
  bauvorhabenId, data, canWrite, onChanged, onReloaded,
}: BauvorhabenAbrechnungCardProps) {
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState<Partial<AbrechnungSettings> | null>(null);

  // Inline-Editor je Sektion (immer nur eine Zeile gleichzeitig)
  const [editNachtragId, setEditNachtragId] = useState<number | null>(null);
  const [nachtragDraft, setNachtragDraft] = useState<Partial<NachtragPayload>>({});
  const [showNachtragForm, setShowNachtragForm] = useState(false);
  const [newNachtrag, setNewNachtrag] = useState<NachtragPayload>({ typ: 'nachtrag', titel: '', betrag_netto: '' });

  const [editBnkId, setEditBnkId] = useState<number | null>(null);
  const [bnkDraft, setBnkDraft] = useState<Partial<BnkPayload>>({});
  const [showBnkForm, setShowBnkForm] = useState(false);
  const [newBnk, setNewBnk] = useState<BnkPayload>({ bezeichnung: '' });

  const [editZpId, setEditZpId] = useState<number | null>(null);
  const [zpDraft, setZpDraft] = useState<Partial<ZahlungsplanPayload>>({});
  const [showZpForm, setShowZpForm] = useState(false);
  const [newZp, setNewZp] = useState<ZahlungsplanPayload>({ art: 'ausgangsrechnung', bezeichnung: '' });

  const run = async (
    fn: () => Promise<{ data: { data: AbrechnungResponse } }>,
    ok: string,
  ) => {
    setIsBusy(true);
    setError('');
    try {
      const response = await fn();
      onReloaded(response.data.data);
      setInfo(ok);
      return true;
    } catch (err) {
      setError(apiError(err, 'Aktion fehlgeschlagen.'));
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calculator className="h-5 w-5 text-gray-400" />
          Abrechnung
        </h2>
        <p className="text-sm text-gray-500">Abrechnungs-Daten konnten nicht geladen werden.</p>
      </div>
    );
  }

  const { settings, nachtraege, bnk, zahlungsplan, summary } = data;

  const saveSettings = async () => {
    if (!settingsDraft) return;
    // Strings/ungenutzte Felder direkt durchreichen — der Server
    // normalisiert deutsche Dezimalnotation (normalizeMoney/Percent).
    const payload: AbrechnungSettingsPatch = {};
    for (const k of ['verkaufssumme_netto', 'verkaufssumme_brutto', 'fehlpositionen_vertrieb', 'vorplanungszuschlag', 'provision_gesamt'] as const) {
      if (k in settingsDraft) payload[k] = (settingsDraft[k] ?? null) as never;
    }
    if ('agk_percent' in settingsDraft) payload.agk_percent = settingsDraft.agk_percent as never;
    if ('mwb_anteil_percent' in settingsDraft) payload.mwb_anteil_percent = settingsDraft.mwb_anteil_percent as never;
    if ('bemerkung' in settingsDraft) payload.bemerkung = settingsDraft.bemerkung ?? null;
    const ok = await run(() => abrechnungApi.updateSettings(bauvorhabenId, payload), 'Abrechnung gespeichert.');
    if (ok) setSettingsDraft(null);
  };

  // Nachträge
  const saveNachtrag = async (rowId: number, payload: Partial<NachtragPayload>) => {
    const ok = await run(() => abrechnungApi.updateNachtrag(bauvorhabenId, rowId, payload), 'Position gespeichert.');
    if (ok) {
      setEditNachtragId(null);
      onChanged();
    }
  };
  const deleteNachtrag = async (row: NachtragRow) => {
    if (!window.confirm(`„${row.titel}" löschen?`)) return;
    if (await run(() => abrechnungApi.removeNachtrag(bauvorhabenId, row.id), 'Position gelöscht.')) onChanged();
  };

  // BNK
  const saveBnk = async (rowId: number, payload: Partial<BnkPayload>) => {
    const ok = await run(() => abrechnungApi.updateBnk(bauvorhabenId, rowId, payload), 'BNK-Position gespeichert.');
    if (ok) {
      setEditBnkId(null);
      onChanged();
    }
  };
  const deleteBnk = async (row: BnkPosition) => {
    if (!window.confirm(`„${row.bezeichnung}" löschen?`)) return;
    if (await run(() => abrechnungApi.removeBnk(bauvorhabenId, row.id), 'BNK-Position gelöscht.')) onChanged();
  };

  // Zahlungsplan
  const toggleBezahlt = async (row: ZahlungsplanRow) => {
    const next = !row.ist_bezahlt;
    if (await run(() => abrechnungApi.updateZahlungsplan(bauvorhabenId, row.id, {
      ist_bezahlt: next,
      bezahlt_am: next ? new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }) : null,
    }), next ? 'Als bezahlt markiert.' : 'Bezahlt-Markierung entfernt.')) onChanged();
  };
  const saveZp = async (rowId: number, payload: Partial<ZahlungsplanPayload>) => {
    const ok = await run(() => abrechnungApi.updateZahlungsplan(bauvorhabenId, rowId, payload), 'Position gespeichert.');
    if (ok) {
      setEditZpId(null);
      onChanged();
    }
  };
  const deleteZp = async (row: ZahlungsplanRow) => {
    if (!window.confirm(`„${row.bezeichnung}" löschen?`)) return;
    if (await run(() => abrechnungApi.removeZahlungsplan(bauvorhabenId, row.id), 'Position gelöscht.')) onChanged();
  };

  // ---- Formelkette-Zeilen (transparent, Excel-Formeln nachgebaut) ----
  const formulaRows: { label: string; value: string; strong?: boolean; hint?: string }[] = [
    { label: 'Herstellsumme (Einkauf inkl. Eigenleistung)', value: formatCurrency(summary.herstellsumme) },
    { label: '+ BNK (EK-Seite)', value: formatCurrency(summary.bnk_summe_ek) },
    { label: '+ Nachträge', value: formatCurrency(summary.nachtraege_summe) },
    { label: '− Gutschriften', value: formatCurrency(summary.gutschriften_summe) },
    { label: '= Herstellkosten', value: formatCurrency(summary.herstellkosten), strong: true },
    {
      label: `× AGK ${summary.agk_percent.toLocaleString('de-DE')} %`,
      value: formatCurrency(summary.agk_betrag),
      hint: `MWB-Anteil ${summary.agk_mwb_betrag.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} · GPS-Rest ${summary.agk_gps_betrag.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`,
    },
    { label: 'Verkaufssumme netto', value: formatCurrency(summary.verkaufssumme_netto) },
    { label: 'G/V (GPS)', value: formatCurrency(summary.gewinn_verlust), strong: true },
  ];

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Calculator className="h-5 w-5 text-gray-400" />
          Abrechnung
        </h2>
        {settings.updated_by_user_name && (
          <span className="text-xs text-gray-400">
            Zuletzt: {settings.updated_by_user_name} · {formatDateTime(settings.updated_at)}
          </span>
        )}
      </div>

      {info && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{info}</div>
      )}
      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      {/* Kopfzeile (nur Finanzen editierbar) */}
      <div className="rounded-lg border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Kopfzeile</h3>
        {settingsDraft === null ? (
          <div className="grid gap-3 sm:grid-cols-4">
            <Kv label="Verkaufssumme netto" value={formatCurrency(settings.verkaufssumme_netto)} />
            <Kv label="Verkaufssumme brutto" value={formatCurrency(settings.verkaufssumme_brutto)} />
            <Kv label="Fehlpositionen Vertrieb" value={formatCurrency(settings.fehlpositionen_vertrieb)} />
            <Kv label="Vorplanungszuschlag" value={formatCurrency(settings.vorplanungszuschlag)} />
            <Kv label="Provision gesamt" value={formatCurrency(settings.provision_gesamt)} />
            <Kv label="AGK" value={`${summary.agk_percent.toLocaleString('de-DE')} %`} />
            <Kv label="MWB-Anteil" value={`${summary.mwb_anteil_percent.toLocaleString('de-DE')} %`} />
            <div className="sm:col-span-4 space-y-1">
              {canWrite && (
                <button type="button" className="btn btn-secondary text-sm" onClick={() => setSettingsDraft({ ...settings })}>
                  <Pencil className="h-3.5 w-3.5" /> Kopfzeile bearbeiten
                </button>
              )}
              {settings.bemerkung && <p className="text-xs text-gray-600">Bemerkung: {settings.bemerkung}</p>}
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Verkaufssumme netto (€)" value={settingsDraft.verkaufssumme_netto} onChange={(v) => setSettingsDraft((d) => ({ ...d, verkaufssumme_netto: v as never }))} placeholder="z. B. 682.184,87" />
              <Field label="Verkaufssumme brutto (€)" value={settingsDraft.verkaufssumme_brutto} onChange={(v) => setSettingsDraft((d) => ({ ...d, verkaufssumme_brutto: v as never }))} placeholder="≈ netto × 1,19" />
              <Field label="Fehlpositionen Vertrieb (€)" value={settingsDraft.fehlpositionen_vertrieb} onChange={(v) => setSettingsDraft((d) => ({ ...d, fehlpositionen_vertrieb: v as never }))} />
              <Field label="Vorplanungszuschlag (€)" value={settingsDraft.vorplanungszuschlag} onChange={(v) => setSettingsDraft((d) => ({ ...d, vorplanungszuschlag: v as never }))} />
              <Field label="Provision gesamt (€)" value={settingsDraft.provision_gesamt} onChange={(v) => setSettingsDraft((d) => ({ ...d, provision_gesamt: v as never }))} />
              <Field label="AGK (%)" value={settingsDraft.agk_percent} onChange={(v) => setSettingsDraft((d) => ({ ...d, agk_percent: v as never }))} />
              <Field label="MWB-Anteil (%)" value={settingsDraft.mwb_anteil_percent} onChange={(v) => setSettingsDraft((d) => ({ ...d, mwb_anteil_percent: v as never }))} />
              <Field label="Bemerkung" value={settingsDraft.bemerkung} onChange={(v) => setSettingsDraft((d) => ({ ...d, bemerkung: v as never }))} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button type="button" className="btn btn-primary text-sm" disabled={isBusy} onClick={saveSettings}>
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Speichern
              </button>
              <button type="button" className="text-sm text-gray-500 hover:text-gray-700" onClick={() => setSettingsDraft(null)}>Abbrechen</button>
            </div>
          </>
        )}
      </div>

      {/* Formelkette */}
      <div className="rounded-lg bg-gray-50 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Formelkette (berechnet)</h3>
        <table className="w-full text-sm">
          <tbody>
            {formulaRows.map((line) => (
              <tr key={line.label} className={line.strong ? 'font-semibold text-gray-900 border-t border-gray-200' : 'text-gray-700'}>
                <td className="py-1 pr-3">{line.label}</td>
                <td className="py-1 text-right tabular-nums">{line.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {summary.vertrieb_ergebnis_basis !== null && (
          <p className="mt-2 text-xs text-gray-500">
            Vertriebsergebnis-Basis = Rohertrag − Fehlpositionen + Vorplanungszuschlag: {formatCurrency(summary.vertrieb_ergebnis_basis)} · Provision gesamt {formatCurrency(summary.provision_gesamt)} · im Zahlungsplan geplant {formatCurrency(summary.provision_geplant)}
          </p>
        )}
      </div>

      {/* Nachträge + Gutschriften */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Nachträge & Gutschriften</h3>
          {canWrite && (
            <button type="button" className="btn btn-secondary text-sm inline-flex items-center gap-1" onClick={() => setShowNachtragForm((v) => !v)}>
              {showNachtragForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {showNachtragForm ? 'Abbrechen' : 'Position'}
            </button>
          )}
        </div>
        {showNachtragForm && (
          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 grid gap-2 sm:grid-cols-6">
            <Field label="Typ" value={`${newNachtrag.typ ?? 'nachtrag'}`} selectOptions={[['nachtrag', 'Nachtrag (+)'], ['gutschrift', 'Gutschrift (−)']]} onChange={(v) => setNewNachtrag((d) => ({ ...d, typ: v as NachtragPayload['typ'] }))} />
            <Field label="Titel" value={newNachtrag.titel ?? ''} onChange={(v) => setNewNachtrag((d) => ({ ...d, titel: v }))} />
            <Field label="Nummer" value={newNachtrag.nummer ?? ''} onChange={(v) => setNewNachtrag((d) => ({ ...d, nummer: v || null }))} />
            <Field label="Datum" type="date" value={newNachtrag.datum ?? ''} onChange={(v) => setNewNachtrag((d) => ({ ...d, datum: v || null }))} />
            <Field label="Betrag netto (€)" value={newNachtrag.betrag_netto ?? ''} onChange={(v) => setNewNachtrag((d) => ({ ...d, betrag_netto: v }))} />
            <div className="flex items-end">
              <button type="button" className="btn btn-primary text-sm" disabled={isBusy || !newNachtrag.titel?.trim()} onClick={async () => {
                if (await run(() => abrechnungApi.addNachtrag(bauvorhabenId, newNachtrag), 'Position angelegt.')) {
                  setNewNachtrag({ typ: 'nachtrag', titel: '', betrag_netto: '' });
                  setShowNachtragForm(false);
                  onChanged();
                }
              }}>Hinzufügen</button>
            </div>
          </div>
        )}
        {nachtraege.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Nachträge oder Gutschriften erfasst.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Typ</th>
                <th className="py-1.5 pr-3 font-medium">Titel</th>
                <th className="py-1.5 pr-3 font-medium">Nummer</th>
                <th className="py-1.5 pr-3 font-medium">Datum</th>
                <th className="py-1.5 pr-3 font-medium text-right">Netto</th>
                <th className="py-1.5 font-medium">Bemerkung</th>
                {canWrite && <th className="py-1.5" aria-label="Aktionen" />}
              </tr>
            </thead>
            <tbody>
              {nachtraege.map((row) => {
                const editing = editNachtragId === row.id;
                return editing ? (
                  <tr key={row.id} className="border-b border-gray-100 bg-blue-50/50">
                    <td className="py-1.5 pr-3" colSpan={5}>
                      <div className="grid gap-2 sm:grid-cols-5">
                        <Field label="Typ" selectOptions={[['nachtrag', 'Nachtrag (+)'], ['gutschrift', 'Gutschrift (−)']]} value={String(nachtragDraft.typ ?? row.typ)} onChange={(v) => setNachtragDraft((d) => ({ ...d, typ: v as NachtragPayload['typ'] }))} />
                        <Field label="Titel" value={String(nachtragDraft.titel ?? row.titel)} onChange={(v) => setNachtragDraft((d) => ({ ...d, titel: v }))} />
                        <Field label="Nummer" value={String(nachtragDraft.nummer ?? row.nummer ?? '')} onChange={(v) => setNachtragDraft((d) => ({ ...d, nummer: v || null }))} />
                        <Field label="Datum" type="date" value={String(nachtragDraft.datum ?? row.datum ?? '')} onChange={(v) => setNachtragDraft((d) => ({ ...d, datum: v || null }))} />
                        <Field label="Betrag netto (€)" value={String(nachtragDraft.betrag_netto ?? row.betrag_netto)} onChange={(v) => setNachtragDraft((d) => ({ ...d, betrag_netto: v }))} />
                      </div>
                    </td>
                    <td className="py-1.5" colSpan={2}>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <button type="button" className="btn btn-primary text-xs" disabled={isBusy} onClick={() => saveNachtrag(row.id, nachtragDraft)}>Speichern</button>
                        <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setEditNachtragId(null)}>Abbrechen</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="py-1.5 pr-3">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${row.typ === 'gutschrift' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {row.typ === 'gutschrift' ? 'Gutschrift' : 'Nachtrag'}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 font-medium text-gray-900">{row.titel}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{row.nummer ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{row.datum ?? '—'}</td>
                    <td className={`py-1.5 pr-3 text-right font-medium ${row.typ === 'gutschrift' ? 'text-red-600' : 'text-green-700'}`}>
                      {formatCurrency(row.betrag_netto)}
                    </td>
                    <td className="py-1.5 text-xs text-gray-600">{row.bemerkung ?? '—'}</td>
                    {canWrite && (
                      <td className="py-1.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => { setEditNachtragId(row.id); setNachtragDraft({ typ: row.typ, titel: row.titel, nummer: row.nummer, datum: row.datum, betrag_netto: String(row.betrag_netto).replace('.', ','), bemerkung: row.bemerkung }); }} className="text-xs text-primary-600 hover:text-primary-700">Bearbeiten</button>
                          <button type="button" onClick={() => deleteNachtrag(row)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* BNK */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Allgemeine BNK (VK + EK netto)</h3>
          {canWrite && (
            <button type="button" className="btn btn-secondary text-sm inline-flex items-center gap-1" onClick={() => setShowBnkForm((v) => !v)}>
              {showBnkForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {showBnkForm ? 'Abbrechen' : 'Position'}
            </button>
          )}
        </div>
        {showBnkForm && (
          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 grid gap-2 sm:grid-cols-7">
            <Field label="Bezeichnung" value={newBnk.bezeichnung ?? ''} onChange={(v) => setNewBnk((d) => ({ ...d, bezeichnung: v }))} />
            <Field label="Menge" value={newBnk.menge === undefined || newBnk.menge === null ? '' : String(newBnk.menge)} onChange={(v) => setNewBnk((d) => ({ ...d, menge: parseGermanDecimal(v) }))} />
            <Field label="Einheit" value={newBnk.einheit ?? ''} onChange={(v) => setNewBnk((d) => ({ ...d, einheit: v || null }))} />
            <Field label="VK-Einzel netto (€)" value={newBnk.vk_einzelpreis_netto ?? ''} onChange={(v) => setNewBnk((d) => ({ ...d, vk_einzelpreis_netto: parseGermanDecimal(v) }))} />
            <Field label="VK gesamt netto (€)" value={newBnk.vk_gesamt_netto ?? ''} onChange={(v) => setNewBnk((d) => ({ ...d, vk_gesamt_netto: parseGermanDecimal(v) }))} />
            <Field label="EK gesamt netto (€)" value={newBnk.ek_gesamt_netto ?? ''} onChange={(v) => setNewBnk((d) => ({ ...d, ek_gesamt_netto: parseGermanDecimal(v) }))} />
            <div className="flex items-end">
              <button type="button" className="btn btn-primary text-sm" disabled={isBusy || !newBnk.bezeichnung?.trim()} onClick={async () => {
                if (await run(() => abrechnungApi.addBnk(bauvorhabenId, newBnk), 'BNK-Position angelegt.')) {
                  setNewBnk({ bezeichnung: '' });
                  setShowBnkForm(false);
                  onChanged();
                }
              }}>Hinzufügen</button>
            </div>
          </div>
        )}
        {bnk.length === 0 ? (
          <p className="text-sm text-gray-500">Keine BNK-Positionen erfasst.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Pos.</th>
                <th className="py-1.5 pr-3 font-medium">Bezeichnung</th>
                <th className="py-1.5 pr-3 font-medium text-right">Menge</th>
                <th className="py-1.5 pr-3 font-medium text-right">VK netto</th>
                <th className="py-1.5 pr-3 font-medium text-right">EK netto</th>
                <th className="py-1.5 font-medium">Bemerkung</th>
                {canWrite && <th className="py-1.5" aria-label="Aktionen" />}
              </tr>
            </thead>
            <tbody>
              {bnk.map((row) => editBnkId === row.id ? (
                <tr key={row.id} className="border-b border-gray-100 bg-blue-50/50">
                  <td className="py-1.5 pr-3" colSpan={5}>
                    <div className="grid gap-2 sm:grid-cols-5">
                      <Field label="Bezeichnung" value={String(bnkDraft.bezeichnung ?? row.bezeichnung)} onChange={(v) => setBnkDraft((d) => ({ ...d, bezeichnung: v }))} />
                      <Field label="Menge" value={String(bnkDraft.menge ?? row.menge ?? '')} onChange={(v) => setBnkDraft((d) => ({ ...d, menge: parseGermanDecimal(v) }))} />
                      <Field label="VK-Einzel netto (€)" value={String(bnkDraft.vk_einzelpreis_netto ?? row.vk_einzelpreis_netto ?? '')} onChange={(v) => setBnkDraft((d) => ({ ...d, vk_einzelpreis_netto: parseGermanDecimal(v) }))} />
                      <Field label="VK gesamt netto (€)" value={String(bnkDraft.vk_gesamt_netto ?? row.vk_gesamt_netto ?? '')} onChange={(v) => setBnkDraft((d) => ({ ...d, vk_gesamt_netto: parseGermanDecimal(v) }))} />
                      <Field label="EK gesamt netto (€)" value={String(bnkDraft.ek_gesamt_netto ?? row.ek_gesamt_netto ?? '')} onChange={(v) => setBnkDraft((d) => ({ ...d, ek_gesamt_netto: parseGermanDecimal(v) }))} />
                    </div>
                  </td>
                  <td className="py-1.5" colSpan={2}>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <button type="button" className="btn btn-primary text-xs" disabled={isBusy} onClick={() => saveBnk(row.id, bnkDraft)}>Speichern</button>
                      <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setEditBnkId(null)}>Abbrechen</button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 text-xs text-gray-600">{row.position ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-medium text-gray-900">{row.bezeichnung}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-600">{row.menge !== null ? `${row.menge.toLocaleString('de-DE')}${row.einheit ? ` ${row.einheit}` : ''}` : '—'}</td>
                  <td className="py-1.5 pr-3 text-right">{formatCurrency(row.vk_gesamt_netto)}</td>
                  <td className="py-1.5 pr-3 text-right">{formatCurrency(row.ek_gesamt_netto)}</td>
                  <td className="py-1.5 text-xs text-gray-600">{row.bemerkung ?? '—'}</td>
                  {canWrite && (
                    <td className="py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditBnkId(row.id); setBnkDraft({ bezeichnung: row.bezeichnung, menge: row.menge, vk_einzelpreis_netto: row.vk_einzelpreis_netto, vk_gesamt_netto: row.vk_gesamt_netto, ek_einzelpreis_netto: row.ek_einzelpreis_netto, ek_gesamt_netto: row.ek_gesamt_netto, bemerkung: row.bemerkung }); }} className="text-xs text-primary-600 hover:text-primary-700">Bearbeiten</button>
                        <button type="button" onClick={() => deleteBnk(row)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 text-xs font-medium text-gray-700">
                <td colSpan={3} className="pt-1.5">Summen</td>
                <td className="pt-1.5 pr-3 text-right">{formatCurrency(summary.bnk_summe_vk)}</td>
                <td className="pt-1.5 pr-3 text-right">{formatCurrency(summary.bnk_summe_ek)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Zahlungsplan */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Zahlungsplan</h3>
          {canWrite && (
            <button type="button" className="btn btn-secondary text-sm inline-flex items-center gap-1" onClick={() => setShowZpForm((v) => !v)}>
              {showZpForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {showZpForm ? 'Abbrechen' : 'Position'}
            </button>
          )}
        </div>
        {showZpForm && (
          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 grid gap-2 sm:grid-cols-6">
            <Field label="Art" selectOptions={[['ausgangsrechnung', 'Ausgangsrechnung'], ['vertriebsprovision', 'Vertriebsprovision']]} value={String(newZp.art ?? 'ausgangsrechnung')} onChange={(v) => setNewZp((d) => ({ ...d, art: v as ZahlungsplanPayload['art'] }))} />
            <Field label="Bezeichnung" value={newZp.bezeichnung ?? ''} onChange={(v) => setNewZp((d) => ({ ...d, bezeichnung: v }))} />
            <Field label="Baustatus" value={newZp.baustatus ?? ''} onChange={(v) => setNewZp((d) => ({ ...d, baustatus: v || null }))} />
            <Field label="%" value={newZp.prozent ?? ''} onChange={(v) => setNewZp((d) => ({ ...d, prozent: parseGermanDecimal(v) }))} />
            <Field label="Netto (€)" value={newZp.betrag_netto ?? ''} onChange={(v) => setNewZp((d) => ({ ...d, betrag_netto: parseGermanDecimal(v) }))} />
            <div className="flex items-end">
              <button type="button" className="btn btn-primary text-sm" disabled={isBusy || !newZp.bezeichnung?.trim()} onClick={async () => {
                if (await run(() => abrechnungApi.addZahlungsplan(bauvorhabenId, newZp), 'Position angelegt.')) {
                  setNewZp({ art: 'ausgangsrechnung', bezeichnung: '' });
                  setShowZpForm(false);
                  onChanged();
                }
              }}>Hinzufügen</button>
            </div>
          </div>
        )}
        {(['ausgangsrechnung', 'vertriebsprovision'] as const).map((art) => {
          const rows = zahlungsplan.filter((r) => r.art === art);
          const sumBrutto = rows.reduce((acc, r) => acc + (r.betrag_brutto ?? r.betrag_netto ?? 0), 0);
          const sumOffen = rows.filter((r) => !r.ist_bezahlt).reduce((acc, r) => acc + (r.betrag_brutto ?? r.betrag_netto ?? 0), 0);
          return (
            <div key={art} className="mb-4">
              <p className="text-xs font-semibold text-gray-600 mb-1">
                {art === 'ausgangsrechnung' ? 'Ausgangsrechnungen' : 'Vertriebsprovision'} · {rows.length} Positionen · brutto {formatCurrency(sumBrutto)} · offen {formatCurrency(sumOffen)}
              </p>
              {rows.length === 0 ? (
                <p className="text-sm text-gray-400">—</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="py-1.5 pr-3 font-medium">Bezeichnung</th>
                      <th className="py-1.5 pr-3 font-medium text-right">%</th>
                      <th className="py-1.5 pr-3 font-medium">RE-Nr.</th>
                      <th className="py-1.5 pr-3 font-medium">Fällig</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Brutto</th>
                      <th className="py-1.5 pr-3 font-medium text-center">Bezahlt</th>
                      {canWrite && <th className="py-1.5" aria-label="Aktionen" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const editing = editZpId === row.id;
                      const brutto = formatCurrency(row.betrag_brutto ?? row.betrag_netto);
                      return editing ? (
                        <tr key={row.id} className="border-b border-gray-100 bg-blue-50/50">
                          <td className="py-1.5 pr-3" colSpan={5}>
                            <div className="grid gap-2 sm:grid-cols-4">
                              <Field label="Bezeichnung" value={String(zpDraft.bezeichnung ?? row.bezeichnung)} onChange={(v) => setZpDraft((d) => ({ ...d, bezeichnung: v }))} />
                              <Field label="RE-Nr." value={String(zpDraft.rechnungsnummer ?? row.rechnungsnummer ?? '')} onChange={(v) => setZpDraft((d) => ({ ...d, rechnungsnummer: v || null }))} />
                              <Field label="Brutto (€)" value={String(zpDraft.betrag_brutto ?? row.betrag_brutto ?? '')} onChange={(v) => setZpDraft((d) => ({ ...d, betrag_brutto: parseGermanDecimal(v) }))} />
                              <Field label="Fällig" type="date" value={String(zpDraft.faellig_am ?? row.faellig_am ?? '')} onChange={(v) => setZpDraft((d) => ({ ...d, faellig_am: v || null }))} />
                            </div>
                          </td>
                          <td className="py-1.5" colSpan={2}>
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              <button type="button" className="btn btn-primary text-xs" disabled={isBusy} onClick={() => saveZp(row.id, zpDraft)}>Speichern</button>
                              <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setEditZpId(null)}>Abbrechen</button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={row.id} className="border-b border-gray-100">
                          <td className="py-1.5 pr-3">
                            <p className="font-medium text-gray-900">{row.bezeichnung}</p>
                            {row.baustatus && <p className="text-xs text-gray-500">{row.baustatus}</p>}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-gray-600">{row.prozent !== null ? `${row.prozent.toLocaleString('de-DE')} %` : '—'}</td>
                          <td className="py-1.5 pr-3 text-gray-600">
                            {row.invoice_number_linked || row.rechnungsnummer ? (
                              <span className="inline-flex items-center gap-1">
                                {row.invoice_id ? <FileDown className="h-3 w-3 text-gray-400" /> : null}
                                {row.invoice_number_linked || row.rechnungsnummer}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="py-1.5 pr-3 text-gray-600">{row.faellig_am ?? '—'}</td>
                          <td className="py-1.5 pr-3 text-right font-medium">{brutto}</td>
                          <td className="py-1.5 pr-3 text-center">
                            {row.ist_bezahlt ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="h-3.5 w-3.5" />{row.bezahlt_am ? row.bezahlt_am : ''}</span>
                            ) : (
                              canWrite ? (
                                <button type="button" onClick={() => toggleBezahlt(row)} className="text-xs text-gray-500 hover:text-green-700">Als bezahlt markieren</button>
                              ) : (
                                <span className="text-xs text-gray-400">offen</span>
                              )
                            )}
                          </td>
                          {canWrite && (
                            <td className="py-1.5 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <button type="button" onClick={() => { setEditZpId(row.id); setZpDraft({ bezeichnung: row.bezeichnung, rechnungsnummer: row.rechnungsnummer, betrag_brutto: row.betrag_brutto !== null ? String(row.betrag_brutto).replace('.', ',') : '', faellig_am: row.faellig_am }); }} className="text-xs text-primary-600 hover:text-primary-700">Bearbeiten</button>
                                <button type="button" onClick={() => deleteZp(row)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Label/Value-Anzeige (Read-Sicht der Kopfzeile). */
function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

interface FieldProps {
  label: string;
  value?: string | number | null;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'date' | 'number';
  selectOptions?: [string, string][];
}

/** Generisches Formular-Feld (Text/Select/Date). */
function Field({ label, value, onChange, placeholder, type = 'text', selectOptions }: FieldProps) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      {selectOptions ? (
        <select className="input text-sm" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {selectOptions.map(([val, lab]) => (
            <option key={val} value={val}>{lab}</option>
          ))}
        </select>
      ) : (
        <input
          className="input text-sm"
          type={type}
          value={value === null || value === undefined ? '' : String(value)}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}